/*

? First, let's import the required components.

*/
import { styled } from "@mui/system";
import React from "react";

import { Autocomplete, CircularProgress, InputAdornment, TextField, Typography, useTheme } from "@mui/material";
/*

* Google Maps Imports

*/
import NearMeIcon from "@mui/icons-material/NearMe";
import { useLoadScript } from "@react-google-maps/api";
import { useRouter } from "next/router";
import usePlacesAutocomplete, { getGeocode, getLatLng, getZipCode } from "use-places-autocomplete";
import { ALLOWED_POSTAL_PREFIXES, ALLOWED_STATES } from "../../lib/data/allowed-locations";
import StateInterface from "../../redux-magic/state-interface";
import { SearchFilterId } from "../../redux-magic/sub-interfaces/search-types";
import { updateSearchFilterAppliedThunk, updateSearchFilterSelectedOptionsThunk } from "../../redux-magic/thunks";
import PpsfFilterGenertateUrl from "../../lib/PpsfFilterGenertateUrl";
/*

* Interface for location

*/

interface Suggestion {
	description: string;
	matched_substrings: Array<{ length: number; offset: number }>;
	place_id: string;
	reference: string;
	structured_formatting: {
		main_text: string;
		main_text_matched_substrings: Array<{ length: number; offset: number }>;
		secondary_text: string;
	};
	terms: Array<{ offset: number; value: string }>;
	types: Array<string>;
}

interface GeolocationCoordinates {
	latitude: number;
	longitude: number;
	altitude: number | null;
	accuracy: number;
	altitudeAccuracy: number | null;
	heading: number | null;
	speed: number | null;
}
interface GeolocationPosition {
	coords: GeolocationCoordinates;
}
interface GMRGCPlusCode {
	compound_code: string;
	global_code: string;
}
interface GMRGCResultsAddressComponent {
	long_name: string;
	short_name: string;
	types: Array<string>;
}
interface GMRGCResultsGeometryLocation {
	lat: number;
	lng: number;
}
interface GMRGCResultsGeometryBoundsAndGeometryViewport {
	northeast: GMRGCResultsGeometryLocation;
	southwest: GMRGCResultsGeometryLocation;
}
interface GMRGCResultsGeometry {
	bounds: GMRGCResultsGeometryBoundsAndGeometryViewport;
	location: GMRGCResultsGeometryLocation;
	location_type: string;
}
interface GMRGCResults {
	address_components: Array<GMRGCResultsAddressComponent>;
	formatted_address: string;
	geometry: GMRGCResultsGeometry;
	viewport: GMRGCResultsGeometryBoundsAndGeometryViewport;
	place_id: string;
	types: Array<string>;
}
interface GoogleMapsReverseGeocodingApiResponse {
	plus_code: GMRGCPlusCode;
	results: Array<GMRGCResults>;
	status: string;
}

/*

& Next, let's create the component.

*/

const Container = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	width: "100%",
	padding: "0.25rem 0.6rem 0.25rem 0.6rem",
	backgroundColor: theme.palette.background.paper,
	// boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.12)",
	borderRadius: "8px",
}));

const OptionContainer = styled("li")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "space-between",
	width: "100%",
	padding: "0rem 0.5rem",
	borderRadius: "10px",
}));

const OptionText = styled(Typography, {
	shouldForwardProp: (prop) => prop !== "isPrimary",
})<any>(({ theme, isPrimary }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	fontSize: "0.875rem",
	lineHeight: "1rem",
	fontWeight: 400,
	color: isPrimary ? theme.palette.primary.main : theme.palette.text.primary,
})) as any;

const OptionSubtext = styled(Typography)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	fontSize: "0.875rem",
	lineHeight: "1.3125rem",
	fontWeight: 400,
	color: theme.palette.text.secondary,
})) as typeof Typography;

const SearchIconContainer = styled("div")(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	width: "2.5rem",
	height: "2.35rem",
	background: theme.palette.background.paper,
	borderRadius: "0.5rem",
}));

const librariesToLoad: "places"[] = ["places"];

const LocationAutoComplete = ({
	search_id,
	dispatch,
	location,
	searchLocation,
	setIsBeegruAvailable,
	isBeegruAvailable,
	search_filter_state,
}: {
	search_id: SearchFilterId;
	dispatch: Function;
	location: StateInterface["search_filters_state"]["location"];
	searchLocation: any;
	setIsBeegruAvailable: Function;
	isBeegruAvailable: "available" | "not_available" | "no_location" | "";
	search_filter_state: StateInterface["search_filters_state"];
}) => {
	const [loading, setLoading] = React.useState(false);
	const theme = useTheme();
	const router = useRouter();
	const [FilterChanged, setFilterChanged] = React.useState<boolean>(false);

	/*

  & let's declare the states for the usePlacesAutocomplete hook

  */
	const {
		value,
		suggestions: { data },
		setValue,
		init,
	} = usePlacesAutocomplete({
		initOnMount: false,
		debounce: 500,
		requestOptions: {
			componentRestrictions: { country: "in" },
		},
	});

	/*

  & let's declare the states for the useLoadScript hook

  */
	const { isLoaded } = useLoadScript({
		googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY_NO_REFERRER_RESTRICTIONS || "",
		libraries: librariesToLoad as any,
	});
	const [options, setOptions] = React.useState<string[]>([]);

	/* trigger search function  */

	const TriggerSearchFunc = (finaladdress: string, longitude: any, latitude: any) => {
		dispatch(
			updateSearchFilterSelectedOptionsThunk({
				searchFilterId: search_id,
				newOptions: [
					{
						id: "location",
						title: "Location",
						value: {
							title: finaladdress,
							coordinates: [longitude, latitude],
						},
					},
				],
			}),
		);
		dispatch(
			updateSearchFilterAppliedThunk({
				searchFilterId: search_id,
				isApplied: true,
			}),
		);
		/*

						* updating local storage with the current location

						*/
		const search_location_local = {
			id: "location",
			title: "Location",
			value: {
				title: finaladdress,
				coordinates: [longitude, latitude],
			},
		};

		const storeInLocalStorage = (key: any, data: any) => {
			localStorage.setItem(key, JSON.stringify(data));
		};

		if (typeof window !== "undefined") {
			storeInLocalStorage("search_location", search_location_local);
		}
		setFilterChanged(true);
		setLoading(false);
	};
	React.useEffect(() => {
		if (FilterChanged) {
			PpsfFilterGenertateUrl(search_filter_state, router);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [FilterChanged]);
	/* 
	
	* get zip code
	
	*/

	const GetZipCode = async ({ placeId }: { placeId?: string }) => {
		if (placeId) {
			const geocodeResults = await getGeocode({ placeId });
			const llResult = getLatLng(geocodeResults[0]);
			const { lat, lng } = llResult;
			const finaladdress = geocodeResults[0].formatted_address || "";

			if (geocodeResults.length === 0) {
				return;
			}

			const result = geocodeResults[0];
			const zipCode = (await getZipCode(result, false)) || "000000";

			let isAvailable = false;
			const state = result.address_components.find((c) => c.types.includes("administrative_area_level_1"))?.long_name;
			/* 
				
				* 2 checks
				* lets check the zip codes starting with [56, 57, 58, 59, 67, 68, 69].
				* Since state level search eg:bangalore has no particular zip code lets do a administrative_area_level_1 check.
				
			*/
			if (zipCode !== "000000") {
				const prefix = parseInt(zipCode.substring(0, 2));
				isAvailable = ALLOWED_POSTAL_PREFIXES.includes(prefix);
			} else if (ALLOWED_STATES.includes(state || "")) {
				isAvailable = true;
			} else null;

			if (isAvailable) {
				// alert("Beegru is available in your area");
				TriggerSearchFunc(finaladdress, lng, lat);
				setIsBeegruAvailable("available");
			} else {
				// alert("Beegru is not available");
				setIsBeegruAvailable("not_available");
			}
		}
	};

	const getCurrentLocation = () => {
		/*
    & Next, let's check if the user has allowed us to use their location and update the corresponding state accordingly.
    */
		if (!navigator.geolocation) {
			return;
		} else {
			setLoading(true);
			/*
      & Next, since it looks like the user has allowed us to use their location, let's get their current location. Let's begin by disabling the location autocomplete input.
      */
			navigator.geolocation.getCurrentPosition(
				async (position: GeolocationPosition) => {
					/*
          & Next, let's extract the latitude and longitude from the position object.
          */
					const { latitude, longitude }: { latitude: number; longitude: number } = position.coords;
					/*
          & Next, let's use Google Maps' Reverse Geocoding API to get the user's current address.
          */
					const googleMapsReverseGeocodingApiResponse: GoogleMapsReverseGeocodingApiResponse = await (
						await fetch(
							"https://maps.googleapis.com/maps/api/geocode/json?latlng=" +
								latitude +
								"," +
								longitude +
								"&key=" +
								(process.env.GOOGLE_MAPS_API_KEY_NO_REFERRER_RESTRICTIONS || ""),
							{ cache: "no-store" },
						)
					).json();

					/*
          & Next, let's extract the user's current address from the API response object.
          */
					const userCurrentAddress: string | undefined =
						googleMapsReverseGeocodingApiResponse?.results[0]?.address_components?.find(
							(address_component: GMRGCResultsAddressComponent) =>
								address_component.types.includes("sublocality_level_1") ||
								address_component.types.includes("sublocality_level_2") ||
								address_component.types.includes("sublocality_level_3"),
						)?.long_name;
					const userCurrentAreas: Array<string> | undefined = googleMapsReverseGeocodingApiResponse?.results.map(
						(result) => {
							return result.address_components?.find(
								(address_component: GMRGCResultsAddressComponent) =>
									address_component.types.includes("sublocality_level_1") ||
									address_component.types.includes("postal_town") ||
									address_component.types.includes("locality"),
							)?.long_name as string;
						},
					);

					let userCurrentArea: string | undefined;

					let resultPos: number = 0;

					for (let i = 0; i < userCurrentAreas.length; i++) {
						if (userCurrentAreas[i] !== "Bengaluru") {
							userCurrentArea = userCurrentAreas[i];
							resultPos = i;
							break;
						}
					}

					/*
          & Next, let's update the corresponding state with the user's current address. Let's also set the userLocationAllowed state to true and the allowUserInputInLocationAutocomplete state to false.
          */
					const label = userCurrentAddress ? userCurrentAddress : userCurrentArea ? userCurrentArea : "";

					const addressPart = userCurrentAddress ?? "";
					const areaPart = userCurrentArea ?? "";

					const finaladdress = `${addressPart}${addressPart && areaPart ? ", " : ""}${areaPart}`;

					setValue(finaladdress);

					const stateName = googleMapsReverseGeocodingApiResponse?.results
						.flatMap((result) => result.address_components)
						.find((ac: GMRGCResultsAddressComponent) => ac.types.includes("administrative_area_level_1"))?.long_name;

					const normalizedState = stateName?.trim().toLowerCase();

					if (ALLOWED_STATES.map((s) => s.toLowerCase()).includes(normalizedState || "")) {
						TriggerSearchFunc(finaladdress, longitude, latitude);
						setIsBeegruAvailable("available");
					} else {
						setIsBeegruAvailable("not_available");
						// setValue("");
					}
				},
				async (error) => {
					/*

					* error handling if user denies location access or if location is not available or if location request times out

					*/
					if (error.code === error.PERMISSION_DENIED) {
						setLoading(false);
						alert("Location access is required. Please enable it in your browser settings.");
					} else if (error.code === error.POSITION_UNAVAILABLE) {
						setLoading(false);
						alert("Unable to determine your location. Please try again.");
					} else if (error.code === error.TIMEOUT) {
						setLoading(false);
					} else {
						console.log("An unknown error occurred:", error.message);
						setLoading(false);
					}
				},
				{ timeout: 10000, enableHighAccuracy: true },
			);
		}
	};

	/*

  & let's define the function to handle the change in the location autocomplete

  */
	const onChangeLocationAutocomplete = async (event: React.SyntheticEvent<HTMLSelectElement>, tvalue: any) => {
		setValue("");
		if (tvalue === "Use current location") {
			getCurrentLocation();
		} else {
			data.find((res) => {
				if (res.place_id === tvalue) {
					setValue(res.structured_formatting.main_text);
					GetZipCode({ placeId: res.place_id });
				}
			});
		}
	};

	/*

  & let's use the useEffect hook to set the value of the location autocomplete to the default location and initialize the autocomplete

  */

	React.useEffect(() => {
		if (isLoaded) {
			init();
		}
	}, [isLoaded, init]);

	React.useEffect(() => {
		/*
		 * Filter the suggestions to only show the ones in Karnataka, India.
		 * If no valid suggestions exist, add a default message.
		 */
		const mapped = ["Use current location", ...data.map((suggestion: Suggestion) => suggestion?.place_id)].filter(
			(option: string, position: number, self: Array<string>) =>
				option !== "" && self.findIndex((item: string) => item === option) === position,
		);
		setOptions(mapped);
	}, [data]);

	React.useEffect(() => {
		if (location?.selected_options.length > 0) {
			const selectedOption = location?.selected_options[0]?.value;
			if (typeof selectedOption === "object" && "title" in selectedOption) {
				setValue(selectedOption.title);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);
	/*

	* if local storage has location. let's update the same in value and search filters location state

	*/

	React.useEffect(() => {
		if (
			location.selected_options.length === 0 &&
			searchLocation &&
			!location.is_applied
			// router.asPath === "/"
		) {
			const selectedOption = searchLocation?.value;
			if (typeof selectedOption === "object" && "title" in selectedOption) {
				const title = selectedOption.title;
				const coordinates = selectedOption.coordinates;
				setValue(selectedOption.title);
				dispatch(
					updateSearchFilterSelectedOptionsThunk({
						searchFilterId: search_id,
						newOptions: [
							{
								id: "location",
								title: "Location",
								value: {
									title: title || "",
									coordinates: [coordinates[0], coordinates[1]],
								},
							},
						],
					}),
				);
				dispatch(
					updateSearchFilterAppliedThunk({
						searchFilterId: search_id,
						isApplied: true,
					}),
				);
				setIsBeegruAvailable("available");
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchLocation, location]);

	const [EnterKey, setEnterKey] = React.useState(false);

	const handleKeyDown = async (event: React.KeyboardEvent) => {
		if (event.key !== "Enter") return;

		if (options.length === 0) {
			setEnterKey(true);
			return;
		}

		const selectedPlaceId = options[1];
		if (!selectedPlaceId) {
			console.warn("No valid place selected from options");
			return;
		}

		setValue(selectedPlaceId);

		const item = data.find((res) => res.place_id === selectedPlaceId);
		if (!item) {
			console.warn("Selected place not found in data");
			return;
		}

		try {
			const geocodeResults = await getGeocode({ placeId: selectedPlaceId });
			if (!geocodeResults?.length) {
				console.warn("No geocode results for place:", selectedPlaceId);
				return;
			}

			const { lat: latitude, lng: longitude } = getLatLng(geocodeResults[0]);
			const finaladdress = item?.structured_formatting?.main_text || "";

			const stateName = geocodeResults
				.flatMap((result) => result.address_components)
				.find((ac) => ac.types.includes("administrative_area_level_1"))
				?.long_name?.trim()
				.toLowerCase();

			const normalizedState = stateName?.trim().toLowerCase();

			if (ALLOWED_STATES.map((s) => s.toLowerCase()).includes(normalizedState || "")) {
				TriggerSearchFunc(finaladdress, longitude, latitude);
				setIsBeegruAvailable("available");
			} else {
				setIsBeegruAvailable("not_available");
				dispatch(
					updateSearchFilterAppliedThunk({
						searchFilterId: search_id,
						isApplied: false,
					}),
				);
			}
		} catch (error) {
			console.error("Error fetching place details:", error);
		}
	};

	React.useEffect(() => {
		if (EnterKey) {
			setTimeout(() => {
				setEnterKey(false);
			}, 2000);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [EnterKey]);

	React.useEffect(() => {
		if (isBeegruAvailable === "not_available") {
			setTimeout(() => {
				setValue("");
				setIsBeegruAvailable("");
			}, 2000);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isBeegruAvailable]);

	return (
		<React.Fragment>
			<Container
				style={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "center",
					alignItems: "center",
					width: "100%",
				}}
			>
				<Autocomplete
					disablePortal
					freeSolo={true}
					id="home_desktop_location_autocomplete"
					filterOptions={() => options}
					options={options}
					isOptionEqualToValue={(option, value) => {
						const item = data.find((res) => res.structured_formatting.main_text === value)?.place_id;
						return !item ? false : option === item;
					}}
					onInputChange={(event, newInputValue) => {
						if (newInputValue === "") {
							setIsBeegruAvailable("no_location");
							dispatch(
								updateSearchFilterAppliedThunk({
									searchFilterId: search_id,
									isApplied: false,
								}),
							);
						}
						setValue(newInputValue);
					}}
					value={value}
					renderOption={({ key, ...props }, option) => {
						const las = data.find((suggestion) => suggestion.place_id === option);

						const optionText =
							option === "Use current location" ? "Use current location" : las?.structured_formatting?.main_text;

						const optionSubtext =
							option === "Use current location"
								? "Use current location"
								: las
									? las?.structured_formatting?.secondary_text
									: undefined;

						if (value === optionText) {
							return null;
						}

						return (
							<OptionContainer
								key={key}
								{...props}
							>
								<OptionText
									variant="body1"
									component="span"
									isPrimary={option === "Use current location"}
								>
									{optionText === "Use current location" && (
										<NearMeIcon
											fontSize={"small"}
											sx={{ marginRight: "0.5rem" }}
										/>
									)}
									{optionText === "Use current location" ? "Use current location" : optionText}
								</OptionText>
								<OptionSubtext
									variant="body2"
									component="span"
									sx={{
										display: optionSubtext === "Use current location" ? "none" : "flex",
									}}
								>
									{optionSubtext ? (optionSubtext === "Use current location" ? "" : optionSubtext) : ""}
								</OptionSubtext>
							</OptionContainer>
						);
					}}
					onChange={onChangeLocationAutocomplete}
					sx={{
						width: "100%",
						height: "100%",
						justifyContent: "center",
						alignItems: "center",
						paddingRight: "0px",
						"& .MuiAutocomplete-listbox": {
							padding: 0, // Remove padding from the listbox
						},
						"&.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot": {
							paddingRight: "0px",
						},
						"&.MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot": {
							paddingRight: "0px",
						},
					}}
					renderInput={(params) => (
						<TextField
							placeholder={
								router.locale === "ar-AE"
									? "أدخل موقعًا..."
									: router.locale === "kn-IN"
										? "ಸ್ಥಳವನ್ನು ನಮೂದಿಸಿ..."
										: "Enter a location..."
							}
							variant="standard"
							{...params}
							onChange={(e) => {
								setValue(e.target.value);
							}}
							error={isBeegruAvailable === "no_location" ? true : false}
							sx={{
								fontSize: "0.875rem",
							}}
							onClick={(e) => {
								/*

										* if value is not empty and user clicks on the input field, then set the value and show the suggestions

									*/
								if (value !== "") {
									setValue(value);
								}
							}}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<InputAdornment position="end">
										{!loading ? null : (
											<CircularProgress
												sx={{
													cursor: "progress",
													color: theme.palette.text.secondary,
												}}
												size={24}
											/>
										)}
									</InputAdornment>
								),
								disableUnderline: true,
							}}
							onKeyDown={(e) => {
								if (value !== "" && options.length > 1) {
									handleKeyDown(e);
								}
							}}
						/>
					)}
				/>
			</Container>
		</React.Fragment>
	);
};

export default LocationAutoComplete;
